import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Text from "@/components/ui/text";
import { ContractData } from "@/pages/contracts/types";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { FinancialRecognitionValues, MetricRow } from "./types";
import {
  buildMetricRows,
  formatForInput,
  parseInputToNumber
} from "./utils";
import MetricsTable from "./MetricsTable";
import EmptyServicesBanner from "./EmptyServicesBanner";

const TRANSLATION_PREFIX = "contracts.contractView.financialRecognitionModal";

interface Props {
  open: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  contract: ContractData;
  onSave: (values: FinancialRecognitionValues) => void;
  /** See `ContractFinancialRecognition` — false drops the CARR/CMRR row. */
  showCommittedValues?: boolean;
}

type RecognizedState = Record<string, string>;

const toRecognizedState = (
  rows: MetricRow[],
  pick: (row: MetricRow) => number | null
): RecognizedState =>
  Object.fromEntries(rows.map((row) => [row.key, formatForInput(pick(row))]));

const FinancialRecognitionModal: React.FC<Props> = ({
  open,
  setIsOpen,
  contract,
  onSave,
  showCommittedValues = true
}) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();
  const currencySymbol = getCurrencySymbol(contract.currency || "USD");

  const rows = useMemo(
    () => buildMetricRows(contract, isArr, t, showCommittedValues),
    [contract, isArr, t, showCommittedValues]
  );

  const initialRecognized = useMemo(
    () =>
      toRecognizedState(rows, (row) => row.recognizedValue ?? row.calculatedValue),
    [rows]
  );

  const [recognized, setRecognized] =
    useState<RecognizedState>(initialRecognized);
  const [note, setNote] = useState(contract.financialNote ?? "");

  useEffect(() => {
    if (open) {
      setRecognized(initialRecognized);
      setNote(contract.financialNote ?? "");
    }
  }, [open, initialRecognized, contract.financialNote]);

  const hasServices = (contract.revenueItems ?? []).some(
    (item) => !item.isDeleted
  );

  const handleResetRow = useCallback((row: MetricRow) => {
    setRecognized((prev) => ({
      ...prev,
      [row.key]: formatForInput(row.calculatedValue)
    }));
  }, []);

  const handleResetAll = useCallback(() => {
    setRecognized(toRecognizedState(rows, (row) => row.calculatedValue));
    setNote("");
  }, [rows]);

  const handleCancel = useCallback(() => setIsOpen(false), [setIsOpen]);

  const handleSave = useCallback(() => {
    const values: FinancialRecognitionValues = {};
    rows.forEach((row) => {
      // Sending a financial *_local field server-side flips
      // `financial_override=True`, freezing the value against future
      // revenue-item-driven recomputes. Only include fields the user
      // actually touched so editing one metric does not silently
      // override the others.
      if ((recognized[row.key] ?? "") !== (initialRecognized[row.key] ?? "")) {
        values[row.recognizedField as keyof FinancialRecognitionValues] =
          parseInputToNumber(recognized[row.key] ?? "") as never;
      }
    });
    const trimmedNote = note.trim() === "" ? null : note.trim();
    if (trimmedNote !== (contract.financialNote ?? null)) {
      values.financialNote = trimmedNote;
    }
    if (Object.keys(values).length === 0) {
      setIsOpen(false);
      return;
    }
    onSave(values);
    setIsOpen(false);
  }, [
    rows,
    recognized,
    initialRecognized,
    note,
    contract.financialNote,
    onSave,
    setIsOpen
  ]);

  const title = (
    <Text variant="lgSubtitleH1">{t(`${TRANSLATION_PREFIX}.title`)}</Text>
  );

  const noteField = (
    <div className="flex flex-col gap-2">
      <Text variant="bodySecondary">
        {t(`${TRANSLATION_PREFIX}.reasonLabel`)}
      </Text>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t(`${TRANSLATION_PREFIX}.reasonPlaceholder`)}
        rows={1}
        className="rounded-lg border-[0.5px] border-border-field-default bg-surface-field-default placeholder:text-tertiary"
        data-test="fin-rec-note"
      />
      <Text variant="bodyTertiary">
        {t(`${TRANSLATION_PREFIX}.reasonHelpText`)}
      </Text>
    </div>
  );

  const content = (
    <div className="flex flex-col gap-4 px-6 py-4">
      <Text variant="bodySecondary">{t(`${TRANSLATION_PREFIX}.subtitle`)}</Text>
      {hasServices ? (
        <>
          <MetricsTable
            rows={rows}
            recognized={recognized}
            setRecognized={setRecognized}
            currencySymbol={currencySymbol}
            onResetRow={handleResetRow}
          />
          {noteField}
        </>
      ) : (
        <EmptyServicesBanner onAddService={handleCancel} />
      )}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between w-full">
      <Button
        variant="tertiary"
        onClick={handleResetAll}
        data-test="fin-rec-reset-all"
      >
        {t(`${TRANSLATION_PREFIX}.resetAll`)}
      </Button>
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={handleCancel}>
          {t(`${TRANSLATION_PREFIX}.cancel`)}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasServices}
          data-test="fin-rec-save"
        >
          {t(`${TRANSLATION_PREFIX}.saveChanges`)}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      setIsOpen={setIsOpen}
      title={title}
      content={content}
      footer={footer}
      contentClassname="max-w-[720px]"
      data-test="financial-recognition-modal"
    />
  );
};

export default FinancialRecognitionModal;
